import { useCallback, useEffect, useRef, useState } from 'react';

const rowKey = (exerciseId, setId) => JSON.stringify([String(exerciseId), String(setId)]);

// Explicit successful additions only. Never infer intent from a query snapshot.
// Start the lifetime at display, not while waiting for a request/refetch.
export function useNewSetRowEffects(scope, displayedExercises, readOnly = false, coachEffects, onCoachDisplayed) {
  const scopeKey = JSON.stringify([scope, readOnly]);
  const owner = useRef({ scopeKey });
  if (owner.current.scopeKey !== scopeKey) owner.current = { scopeKey };
  const [entries, setEntries] = useState([]);
  const timers = useRef(new Map());
  const consumed = useRef(new Set());
  const mounted = useRef(true);
  const capture = () => owner.current;
  const isCurrent = token => mounted.current && token === owner.current && !readOnly;
  const publish = useCallback((exerciseId, setId, token) => {
    if (!mounted.current || token !== owner.current || readOnly || !setId) return;
    const key = rowKey(exerciseId, setId);
    setEntries(current => current.some(entry => entry.key === key && entry.owner === token) ? current : [
      ...current.filter(entry => entry.owner === token).slice(-63),
      { key, owner: token, expires: Date.now() + 10000, active: false },
    ]);
  }, [readOnly]);

  useEffect(() => {
    mounted.current = true;
    const ownedTimers = timers.current;
    return () => { mounted.current = false; ownedTimers.forEach(clearTimeout); ownedTimers.clear(); };
  }, []);
  useEffect(() => {
    timers.current.forEach(clearTimeout); timers.current.clear();
    setEntries([]);
    consumed.current.clear();
  }, [scopeKey]);
  useEffect(() => {
    if (readOnly || String(coachEffects?.scopeKey) !== String(scope[0])) return;
    for (const target of coachEffects?.targets || []) {
      const delivery = JSON.stringify([
        coachEffects.scopeKey, target.actionId, target.workoutExerciseId, target.setId,
      ].map(String));
      if (consumed.current.has(delivery)) continue;
      const visible = displayedExercises.some(exercise => String(exercise._id) === String(target.workoutExerciseId)
        && exercise.sets?.some(set => String(set._id) === String(target.setId)));
      if (!visible) continue;
      consumed.current.add(delivery);
      if (consumed.current.size > 128) consumed.current.delete(consumed.current.values().next().value);
      publish(target.workoutExerciseId, target.setId, owner.current);
      onCoachDisplayed?.({ ...target, generation: coachEffects.generation, scopeKey: coachEffects.scopeKey });
    }
  }, [coachEffects, displayedExercises, onCoachDisplayed, publish, readOnly, scope]);
  useEffect(() => {
    const retainedKeys = new Set(entries.map(entry => entry.key));
    for (const [key, timer] of timers.current) {
      if (!retainedKeys.has(key)) { clearTimeout(timer); timers.current.delete(key); }
    }
    const visible = new Set(displayedExercises.flatMap(exercise => (exercise.sets || []).map(set => rowKey(exercise._id, set._id))));
    for (const entry of entries) {
      if (entry.owner !== owner.current) continue;
      if (entry.active && !visible.has(entry.key)) {
        clearTimeout(timers.current.get(entry.key));
        timers.current.delete(entry.key);
        setEntries(current => current.filter(item => item !== entry));
      } else if (!entry.active && visible.has(entry.key)) {
        clearTimeout(timers.current.get(entry.key));
        setEntries(current => current.map(item => item === entry ? { ...item, active: true } : item));
        timers.current.set(entry.key, setTimeout(() => {
          setEntries(current => current.filter(item => item.key !== entry.key || item.owner !== entry.owner));
          timers.current.delete(entry.key);
        }, 1450));
      } else if (!timers.current.has(entry.key)) {
        timers.current.set(entry.key, setTimeout(() => {
          setEntries(current => current.filter(item => item !== entry));
          timers.current.delete(entry.key);
        }, Math.max(0, entry.expires - Date.now())));
      }
    }
  }, [entries, displayedExercises]);
  const className = (exerciseId, setId) => !readOnly && entries.some(entry => entry.owner === owner.current
    && entry.active && entry.key === rowKey(exerciseId, setId)) ? ' new-set-row-effect' : '';
  return { capture, isCurrent, publish, className };
}
